import { combineReducers, configureStore, isAnyOf, UnknownAction } from '@reduxjs/toolkit';
import authReducer, { forceLogout, logoutUser } from './slices/authSlice';
import profileReducer from './slices/profileSlice';
import feedReducer from './slices/feedSlice';
import subscriptionReducer from './slices/subscriptionSlice';
import messagingReducer from './slices/messagingSlice';
import verificationReducer from './slices/verificationSlice';
import uiReducer from './slices/uiSlice';
import settingsReducer from './slices/settingsSlice';
import likesReducer from './slices/likesSlice';
import rosesReducer from './slices/rosesSlice';
import activityReducer from './slices/activitySlice';
import eventsReducer from './slices/eventsSlice';
import notificationsReducer from './slices/notificationsSlice';
import storiesReducer from './slices/storiesSlice';
import blogReducer from './slices/blogSlice';
import consentReducer from './slices/consentSlice';
import { setSessionExpiredHandler } from './api/apiClient';

const appReducer = combineReducers({
  auth: authReducer,
  profile: profileReducer,
  feed: feedReducer,
  subscription: subscriptionReducer,
  messaging: messagingReducer,
  verification: verificationReducer,
  ui: uiReducer,
  settings: settingsReducer,
  likes: likesReducer,
  roses: rosesReducer,
  activity: activityReducer,
  events: eventsReducer,
  notifications: notificationsReducer,
  stories: storiesReducer,
  blog: blogReducer,
  consent: consentReducer,
});

type AppState = ReturnType<typeof appReducer>;

const isSessionEnded = isAnyOf(logoutUser.fulfilled, logoutUser.rejected, forceLogout);

// Every slice omitted here falls back to its initial state, so the next user
// signing in on this tab never sees the previous user's data.
function rootReducer(state: AppState | undefined, action: UnknownAction): AppState {
  if (state && isSessionEnded(action)) {
    const { auth, ui, settings, blog, consent } = state;
    return appReducer({ auth, ui, settings, blog, consent }, action);
  }
  return appReducer(state, action);
}

export const store = configureStore({ reducer: rootReducer });

setSessionExpiredHandler(() => {
  if (store.getState().auth.isAuthenticated) store.dispatch(forceLogout());
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
